<template>
  <v-card>
    <v-card-text>
      <v-data-table
        v-bind="dataTableAttrs"
        :headers="headersShown"
        :items="list"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :items-per-page.sync="itemsPerPage"
        v-model="selectedItems"
        @update:items-per-page="handleItemsPerPageUpdated"
        :show-select="false"
      >
        <template v-slot:top>
          <rs-table-title-bar
            v-if="$refs.headSearch"
            title="Faturalar"
            icon="mdi-cash-100"
            :add-route="{ name: 'admin.crm.customer-invoices.create' }"
            @clear-filters="$refs.headSearch.reset"
            @reload="loadList"
            hide-edit
            :search.sync="search.query"
            v-bind="titleBarAttrs"
          ></rs-table-title-bar>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.prepend="{ headers }">
          <rs-table-head-search
            :headers="headers"
            :search.sync="search"
            ref="headSearch"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.append="{ headers }">
          <rs-table-foot-totals
            :headers="headers"
            :totals="footTotals"
            v-if="list.length > 0"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.edit="{ item }">
          <router-link
            class="btn btn-icon btn-sm btn-clean"
            :to="{
              name: 'admin.crm.customer-invoices.edit',
              params: { id: item.id },
            }"
          >
            <i class="menu-icon mdi mdi-pencil"></i>
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.customer_name="{ item }">
          <router-link
            :to="{
              name: 'admin.crm.customers.edit',
              params: {
                id: item.customer_id,
              },
            }"
          >
            {{ item.customer_name }}
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.is_collected="{ value }">
          <rs-table-cell-boolean :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.is_auto_invoiced="{ value }">
          <rs-table-cell-boolean :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.has_accounting="{ value }">
          <rs-table-cell-boolean :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.is_registered="{ value }">
          <rs-table-cell-boolean :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.accounting_registration_status="{ value, item }">
          <span v-if="value">
            {{ getRegistrationStatusText(value) }}
            <small v-if="item.accounting_registration_checked_at" class="d-block text-muted">
              {{ formatDateTime(item.accounting_registration_checked_at) }}
            </small>
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.workflow_status="{ item }">
          <span>
            {{ getWorkflowStatusText(item) }}
            <small v-if="getWorkflowStatusDate(item)" class="d-block text-muted">
              {{ formatDateTime(getWorkflowStatusDate(item)) }}
            </small>
          </span>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.amount="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.amount_tax_included="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.amount_collected="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.amount_remaining="{ value }">
          <rs-table-cell-number price muted-zeros :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.invoiced_on="{ value }">
          <rs-table-cell-date :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.created_at="{ value }">
          <rs-table-cell-date show-time :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.updated_at="{ value }">
          <rs-table-cell-date show-time :value="value" />
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { debounce } from "@/core/plugins/debounce";
import { filtersToURL, hasExport, hasPermissions, isPage } from "@/view/mixins";
import {
  getInvoiceRegistrationStatusText,
  getInvoiceWorkflowStatusDate,
  getInvoiceWorkflowStatusText,
} from "./invoiceWorkflow";

export default {
  name: "AdminCustomerInvoiceList",
  mixins: [filtersToURL, hasExport, hasPermissions, isPage],
  data() {
    return {
      options: {
        sortBy: ["invoiced_on"],
        sortDesc: [true],
      },
      dataTableAttrs: {
        footerProps: {
          disableItemsPerPage: false,
        },
      },
      pageMeta: {
        title: "Müşteri Faturaları",
      },
      headers: [
        {
          text: "",
          value: "edit",
          searchable: false,
          sortable: false,
          width: "30px",
          align: "center",
        },
        {
          text: "Müşteri",
          value: "customer_name",
          searchable: "text",
          width: "250px",
        },
        {
          text: "Tarih",
          value: "invoiced_on",
          searchable: "date",
          align: "end",
        },
        {
          text: "Tutar",
          value: "amount",
          searchable: "number",
          align: "end",
        },
        {
          text: "Tutar (KDV Dahil)",
          value: "amount_tax_included",
          searchable: "number",
          align: "end",
        },
        {
          text: "Ödeme Tutarı",
          value: "amount_collected",
          searchable: "number",
          align: "end",
        },
        {
          text: "Kalan Tutar",
          value: "amount_remaining",
          searchable: "number",
          align: "end",
        },
        {
          text: "Ödenmiş",
          value: "is_collected",
          searchable: "yesNo",
          align: "center",
        },
        {
          text: "Otomatik",
          value: "is_auto_invoiced",
          searchable: "yesNo",
          align: "center",
        },
        {
          text: "Paraşüt",
          value: "has_accounting",
          searchable: "yesNo",
          align: "center",
        },
        {
          text: "Resmi",
          value: "is_registered",
          searchable: "yesNo",
          align: "center",
        },
        {
          text: "Resmileştirme",
          value: "accounting_registration_status",
          searchable: false,
          sortable: false,
          width: "180px",
        },
        {
          text: "Durum",
          value: "workflow_status",
          searchable: false,
          sortable: false,
          width: "220px",
        },
        {
          text: "Oluşturma",
          value: "created_at",
          searchable: "date",
          width: "180px",
        },
        {
          text: "Düzenleme",
          value: "updated_at",
          searchable: "date",
          width: "180px",
        },
      ],
      titleBarAttrs: {
        exportUrl: () => "api-super-admin/customer-invoices",
        exportParams: this.getParams,
      },
    };
  },
  mounted() {
    this.generateExportColumns(this.headers);
  },
  methods: {
    formatDateTime(value) {
      if (!value) {
        return null;
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString("tr-TR");
    },
    getRegistrationStatusText: getInvoiceRegistrationStatusText,
    getWorkflowStatusText: getInvoiceWorkflowStatusText,
    getWorkflowStatusDate: getInvoiceWorkflowStatusDate,
    getParams() {
      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      return params;
    },
    loadList() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.list = [];
      this.footTotals = [];

      const params = this.getParams();

      this.$api
        .query("api-super-admin/customer-invoices", { params })
        .then((response) => {
          this.loadListFromResponse(response);
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }, 250),
      deep: true,
    },
  },
};
</script>
